/**
 * The node's group: pick one (or none), jump to it, or create a new group holding this node.
 * A new group is created inside the node's current group, so groups can be refined in place.
 */
import { For, Show, createMemo, createSignal, type JSX } from "solid-js";
import type { Node } from "@protocol/Node";
import { useApp } from "../../app/context";
import { groupChoices, groupPath } from "../../shared/analysis";
import { toast } from "../../shared/toast";

/** The "Group" section of the node inspector. */
export function NodeGroup(props: { node: () => Node }): JSX.Element {
  const n = props.node;
  const { graph, actions, view } = useApp();
  const choices = createMemo(() => groupChoices(graph.state.graph));
  const path = createMemo(() => (n().group ? groupPath(graph.state.graph, n().group!) : []));
  const [name, setName] = createSignal("");

  /** Creates a group named as typed, holding this node, inside its current group. */
  const create = async () => {
    const wanted = name().trim();
    if (!wanted) return toast("Type a name for the new group.");
    const changes = await actions.addGroup(wanted, n().group ?? null, [n().id]);
    const added = changes?.find((c) => c.type === "group_added");
    if (added?.type === "group_added") {
      setName("");
      toast(`${n().name} is now in the group ${added.group.name}.`);
    }
  };

  return (
    <section class="grp">
      <h3>Group</h3>
      <div class="stack">
        <div class="row-inline">
          <select class="select" id="node-group" aria-label="Group" onChange={(e) => void actions.updateNode(n().id, { group: e.currentTarget.value })}>
            <option value="" selected={!n().group}>
              No group
            </option>
            <For each={choices()}>
              {(c) => (
                <option value={c.id} selected={c.id === n().group}>
                  {c.label}
                </option>
              )}
            </For>
          </select>
          <Show when={n().group}>
            <button type="button" class="btn" id="show-node-group" title={path().map((g) => g.name).join(" › ")} onClick={() => view.selectGroup(n().group!)}>
              Show
            </button>
          </Show>
        </div>
        <div class="row-inline">
          <input
            class="field"
            id="new-group-name"
            maxlength="60"
            autocomplete="off"
            placeholder={n().group ? `New group inside ${path().at(-1)?.name ?? "it"}` : "New group"}
            value={name()}
            onInput={(e) => setName(e.currentTarget.value)}
            onKeyDown={(e) => e.key === "Enter" && void create()}
          />
          <button type="button" class="btn" id="new-group" onClick={() => void create()}>
            Create
          </button>
        </div>
      </div>
    </section>
  );
}
